import { CombatState, PawnState, TaskType, TILE_SIZE } from '@dreamgame/shared';
import { Container, Graphics, Text } from 'pixi.js';
import { getInterpolatedPosition, type PawnData, pawns, playerId } from '../../state/entities';
import { isSelected } from '../../state/selection';
import { getCamera } from '../Camera';
import { COLORS, PAWN_OFFSET, PAWN_RENDER_SIZE } from '../constants';
import * as PawnAnimation from '../PawnAnimation';

// ============================================================================
// PawnLayer - Renders pawns with animations
// ============================================================================

let container: Container;
let nameLabelContainer: Container;
let graphics: Graphics;
let lastDelta = 0;

// ============================================================================
// PERFORMANCE: Preallocated Objects Pattern
// ============================================================================
// Objects prefixed with underscore are preallocated and reused each frame.
// This avoids creating new objects in the render loop which would cause:
// 1. Memory allocation overhead (even small objects add up at 60fps)
// 2. Garbage collection pressure leading to periodic GC pauses
// 3. Microstutter even when FPS counter shows steady 60
//
// IMPORTANT: These objects are mutated in-place. Callers must:
// - Use returned values immediately within the same frame
// - Never store references to these objects long-term
// - Never assume values persist across frames
// ============================================================================
const _activeIds = new Set<number>();
const _pawnCenterResult = { x: 0, y: 0 };
const pawnNameLabels: Map<
  number,
  {
    readonly container: Container;
    readonly background: Graphics;
    readonly text: Text;
    isDowned: boolean;
  }
> = new Map();

const PAWN_NAME_LABEL = {
  offsetY: 2,
  paddingX: 3,
  paddingY: 1,
  cornerRadius: 2,
} as const;

const NAME_LABEL_RESOLUTION_MIN = 2;
const NAME_LABEL_RESOLUTION_MAX = 6;
let _nameLabelResolution = NAME_LABEL_RESOLUTION_MIN;

const PAWN_NAME_STYLE = {
  fontFamily: 'Arial',
  fontSize: 8,
  fill: 0xffffff,
  stroke: { color: 0x000000, width: 1.2 },
} as const;

const DOWNED_PAWN_NAME_COLOR = 0xff0000;
const NORMAL_PAWN_NAME_COLOR = 0xffffff;
const DOWNED_PAWN_NAME_STROKE = { color: 0xfff3f3, width: 1.2 } as const;
const NORMAL_PAWN_NAME_STROKE = { color: 0x000000, width: 1.2 } as const;

export const createPawnLayer = (): { container: Container; graphics: Graphics } => {
  for (const label of pawnNameLabels.values()) {
    label.container.destroy({ children: true });
  }
  pawnNameLabels.clear();

  container = new Container();
  nameLabelContainer = new Container();
  container.addChild(nameLabelContainer);
  graphics = new Graphics();
  return { container, graphics };
};

export const getPawnContainer = (): Container => container;

export const setLastDelta = ({ delta }: { delta: number }): void => {
  lastDelta = delta;
};

// Render pawn with sprites
const renderPawnSprite = ({
  pawn,
  worldX,
  worldY,
  isOwned,
}: {
  pawn: PawnData;
  worldX: number;
  worldY: number;
  isOwned: boolean;
}): void => {
  const spriteData = PawnAnimation.updatePawnSprite({
    pawn,
    parentContainer: container,
    delta: lastDelta,
  });

  if (spriteData) {
    spriteData.container.x = worldX;
    spriteData.container.y = worldY;
    if (spriteData.activeSprite) {
      spriteData.activeSprite.tint = isOwned ? 0xffffff : 0xffccaa;
    }
  }
};

// Render pawn with fallback graphics
const renderPawnFallback = ({
  worldX,
  worldY,
  isOwned,
}: {
  worldX: number;
  worldY: number;
  isOwned: boolean;
}): void => {
  const bodyColor = isOwned ? COLORS.PAWN_OWNED : COLORS.PAWN_ENEMY;
  graphics.setFillStyle({ color: bodyColor });
  graphics.roundRect(
    worldX + PAWN_OFFSET,
    worldY + PAWN_OFFSET,
    PAWN_RENDER_SIZE,
    PAWN_RENDER_SIZE,
    4
  );
  graphics.fill();
};

// Draw selection outline
const drawSelectionOutline = ({ worldX, worldY }: { worldX: number; worldY: number }): void => {
  graphics.setStrokeStyle({ width: 2, color: COLORS.SELECTION_STROKE });
  graphics.roundRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4, 6);
  graphics.stroke();
};

// Draw task indicators for active work
const drawTaskIndicatorIfNeeded = ({
  pawn,
  worldX,
  worldY,
}: {
  pawn: PawnData;
  worldX: number;
  worldY: number;
}): void => {
  if (pawn.state === PawnState.SLEEPING) {
    drawSleepingIndicator({ worldX, worldY, networkId: pawn.networkId });
    return;
  }

  if (pawn.task?.taskType === TaskType.HARVEST && pawn.task.progress > 0) {
    drawPawnTaskProgress({ worldX, worldY, progress: pawn.task.progress });
    return;
  }

  if (pawn.task?.taskType === TaskType.FISH) {
    drawFishingActivityIndicator({ worldX, worldY, networkId: pawn.networkId });
  }
};

const drawZGlyph = ({
  x,
  y,
  size,
  alpha,
}: {
  x: number;
  y: number;
  size: number;
  alpha: number;
}): void => {
  const half = size * 0.5;
  const topY = y - half;
  const bottomY = y + half;
  const leftX = x - half;
  const rightX = x + half;

  graphics.setStrokeStyle({
    width: Math.max(1, size * 0.22),
    color: COLORS.SLEEP_INDICATOR_Z,
    alpha,
  });
  graphics.moveTo(leftX, topY);
  graphics.lineTo(rightX, topY);
  graphics.lineTo(leftX, bottomY);
  graphics.lineTo(rightX, bottomY);
  graphics.stroke();
};

const drawSleepingIndicator = ({
  worldX,
  worldY,
  networkId,
}: {
  worldX: number;
  worldY: number;
  networkId: number;
}): void => {
  const baseX = worldX + TILE_SIZE * 0.58;
  const baseY = worldY + PAWN_OFFSET - 2;
  const time = performance.now() * 0.0017 + networkId * 0.37;

  for (let i = 0; i < 3; i++) {
    const phase = (time + i * 0.33) % 1;
    const rise = phase * 12;
    const drift = phase * 4;
    const size = 4 + i * 1.1 + phase * 1.5;
    const alpha = 0.18 + (1 - phase) * 0.5;
    const x = baseX + i * 4 + drift;
    const y = baseY - i * 6 - rise;

    drawZGlyph({ x, y, size, alpha });
  }
};

const updatePawnNameBackground = ({
  background,
  text,
}: {
  background: Graphics;
  text: Text;
}): void => {
  const width = text.width + PAWN_NAME_LABEL.paddingX * 2;
  const height = text.height + PAWN_NAME_LABEL.paddingY * 2;

  background.clear();
  background.setFillStyle({ color: 0x111111, alpha: 0.3 });
  background.roundRect(
    -width / 2,
    -PAWN_NAME_LABEL.paddingY,
    width,
    height,
    PAWN_NAME_LABEL.cornerRadius
  );
  background.fill();
};

const applyPawnNameTextState = ({ text, isDowned }: { text: Text; isDowned: boolean }): void => {
  text.style.fill = isDowned ? DOWNED_PAWN_NAME_COLOR : NORMAL_PAWN_NAME_COLOR;
  text.style.stroke = isDowned ? DOWNED_PAWN_NAME_STROKE : NORMAL_PAWN_NAME_STROKE;
};

const computeNameLabelResolution = ({ zoom }: { zoom: number }): number => {
  const scaled = Math.ceil(zoom * 2);
  if (scaled < NAME_LABEL_RESOLUTION_MIN) {
    return NAME_LABEL_RESOLUTION_MIN;
  }
  if (scaled > NAME_LABEL_RESOLUTION_MAX) {
    return NAME_LABEL_RESOLUTION_MAX;
  }
  return scaled;
};

const updateNameLabelResolutionIfNeeded = (): void => {
  const nextResolution = computeNameLabelResolution({ zoom: getCamera().zoom });
  if (nextResolution === _nameLabelResolution) {
    return;
  }
  _nameLabelResolution = nextResolution;

  for (const label of pawnNameLabels.values()) {
    label.text.resolution = _nameLabelResolution;
    updatePawnNameBackground({
      background: label.background,
      text: label.text,
    });
  }
};

const drawPawnNameLabel = ({
  pawn,
  worldX,
  worldY,
}: {
  pawn: PawnData;
  worldX: number;
  worldY: number;
}): void => {
  const displayName = pawn.name;
  const isDowned = pawn.isDowned > 0;
  if (displayName.length === 0) {
    return;
  }

  const existingLabel = pawnNameLabels.get(pawn.networkId);
  if (existingLabel) {
    if (existingLabel.text.text !== displayName) {
      existingLabel.text.text = displayName;
      updatePawnNameBackground({
        background: existingLabel.background,
        text: existingLabel.text,
      });
    }
    if (existingLabel.isDowned !== isDowned) {
      applyPawnNameTextState({ text: existingLabel.text, isDowned });
      existingLabel.isDowned = isDowned;
    }
    existingLabel.container.x = worldX + TILE_SIZE / 2;
    existingLabel.container.y = worldY + TILE_SIZE + PAWN_NAME_LABEL.offsetY;
    existingLabel.container.visible = true;
    return;
  }

  const text = new Text({
    text: displayName,
    style: PAWN_NAME_STYLE,
    resolution: _nameLabelResolution,
  });
  text.anchor.set(0.5, 0);
  text.roundPixels = true;
  applyPawnNameTextState({ text, isDowned });

  const background = new Graphics();
  updatePawnNameBackground({ background, text });

  const label = new Container();
  label.x = worldX + TILE_SIZE / 2;
  label.y = worldY + TILE_SIZE + PAWN_NAME_LABEL.offsetY;
  label.addChild(background);
  label.addChild(text);
  nameLabelContainer.addChild(label);

  pawnNameLabels.set(pawn.networkId, {
    container: label,
    background,
    text,
    isDowned,
  });
};

const cleanupRemovedPawnNameLabels = ({
  activeNetworkIds,
}: {
  activeNetworkIds: Set<number>;
}): void => {
  for (const [networkId, label] of pawnNameLabels) {
    if (!activeNetworkIds.has(networkId)) {
      label.container.destroy({ children: true });
      pawnNameLabels.delete(networkId);
    }
  }
};

export const drawPawns = (): void => {
  graphics.clear();
  _activeIds.clear();
  updateNameLabelResolutionIfNeeded();

  const allPawnsList = pawns.value.values();
  const currentPlayerId = playerId.value;
  const useSprites = PawnAnimation.arePawnTexturesLoaded();

  for (const pawn of allPawnsList) {
    _activeIds.add(pawn.networkId);

    const pos = getInterpolatedPosition({ pawn });
    const worldX = pos.x * TILE_SIZE;
    const worldY = pos.y * TILE_SIZE;
    const isOwned = pawn.ownerId === currentPlayerId;

    if (useSprites) {
      renderPawnSprite({ pawn, worldX, worldY, isOwned });
    } else {
      renderPawnFallback({ worldX, worldY, isOwned });
    }

    if (isSelected({ networkId: pawn.networkId })) {
      drawSelectionOutline({ worldX, worldY });
    }

    if (pawn.combatState > CombatState.NONE) {
      drawPawnHealthBar({ worldX, worldY, pawn });
    }
    drawTaskIndicatorIfNeeded({ pawn, worldX, worldY });
    drawPawnNameLabel({ pawn, worldX, worldY });
  }

  if (useSprites) {
    PawnAnimation.cleanupRemovedPawns({ activeNetworkIds: _activeIds });
  }
  cleanupRemovedPawnNameLabels({ activeNetworkIds: _activeIds });
};

const drawFishingActivityIndicator = ({
  worldX,
  worldY,
  networkId,
}: {
  worldX: number;
  worldY: number;
  networkId: number;
}): void => {
  const centerX = worldX + TILE_SIZE / 2;
  const centerY = worldY - 14; // Above health bar
  const t = performance.now() * 0.006 + networkId * 0.73;

  const rippleA = (Math.sin(t) + 1) * 0.5;
  const rippleB = (Math.sin(t + Math.PI) + 1) * 0.5;

  graphics.setFillStyle({ color: COLORS.FISHING_INDICATOR_CORE, alpha: 0.85 });
  graphics.circle(centerX, centerY, 3.5);
  graphics.fill();

  graphics.setStrokeStyle({
    width: 1.2,
    color: COLORS.FISHING_INDICATOR_RING,
    alpha: 0.15 + rippleA * 0.35,
  });
  graphics.circle(centerX, centerY, 6 + rippleA * 3);
  graphics.stroke();

  graphics.setStrokeStyle({
    width: 1,
    color: COLORS.FISHING_INDICATOR_RING,
    alpha: 0.12 + rippleB * 0.25,
  });
  graphics.circle(centerX, centerY, 9 + rippleB * 3);
  graphics.stroke();
};

const drawPawnHealthBar = ({
  worldX,
  worldY,
  pawn,
}: {
  worldX: number;
  worldY: number;
  pawn: PawnData;
}): void => {
  const healthBarY = worldY - 8;

  // Background
  graphics.setFillStyle({ color: COLORS.HEALTH_BACKGROUND });
  graphics.rect(worldX + 4, healthBarY, TILE_SIZE - 8, 4);
  graphics.fill();

  // Health bar
  const healthPercent = pawn.health / pawn.maxHealth;
  const healthColor =
    healthPercent > 0.6
      ? COLORS.HEALTH_HIGH
      : healthPercent > 0.3
        ? COLORS.HEALTH_MEDIUM
        : COLORS.HEALTH_LOW;

  graphics.setFillStyle({ color: healthColor });
  graphics.rect(worldX + 4, healthBarY, (TILE_SIZE - 8) * healthPercent, 4);
  graphics.fill();
};

const drawPawnTaskProgress = ({
  worldX,
  worldY,
  progress,
}: {
  worldX: number;
  worldY: number;
  progress: number;
}): void => {
  const centerX = worldX + TILE_SIZE / 2;
  const centerY = worldY - 14; // Above health bar
  const radius = 6;

  // Background circle
  graphics.setFillStyle({ color: COLORS.TASK_PROGRESS_BG, alpha: 0.7 });
  graphics.circle(centerX, centerY, radius);
  graphics.fill();

  // Progress arc (clockwise from top)
  if (progress > 0) {
    const startAngle = -Math.PI / 2; // Start at top
    const endAngle = startAngle + progress * Math.PI * 2;
    graphics.setFillStyle({ color: COLORS.TASK_PROGRESS_FILL });
    graphics.moveTo(centerX, centerY);
    graphics.arc(centerX, centerY, radius, startAngle, endAngle);
    graphics.lineTo(centerX, centerY);
    graphics.fill();
  }
};

// PERFORMANCE: Helper for path drawing - uses preallocated object
// Caller must use values immediately, not store reference
export const getPawnCenter = ({
  pos,
}: {
  pos: { x: number; y: number };
}): Readonly<{ x: number; y: number }> => {
  const worldX = pos.x * TILE_SIZE;
  const worldY = pos.y * TILE_SIZE;
  _pawnCenterResult.x = worldX + PAWN_OFFSET + PAWN_RENDER_SIZE / 2;
  _pawnCenterResult.y = worldY + PAWN_OFFSET + PAWN_RENDER_SIZE / 2;
  return _pawnCenterResult;
};
